import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NgForage } from 'ngforage';
import { EX_EDIT_ADJUSTMENT_KEY } from 'src/app/components/ex-edit-adjustment/ex-edit-adjustment-strings/ex-edit-adjustment-strings';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper, { TitleOption } from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import ProjectContact from 'src/app/model/company/projectContact';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardArrows from 'src/app/model/product/ip-board/ip-board-arrows';
import IPBoardPlacement from 'src/app/model/product/ip-board/ip-board-placement';
import SecondBoard from 'src/app/model/product/ip-board/second-board';
import Product from 'src/app/model/product/product';
import { Adjustment } from 'src/app/model/project/adjustment';
import { Project } from 'src/app/model/project/project';
import { Repo } from 'src/app/model/project/repo';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import {
  ProjectDisplayObservableService,
  SubProjectStatusChangedEvent,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';

@Component({
  selector: 'client-adjustment-form',
  templateUrl: './client-adjustment-form.component.html',
  styleUrls: ['./client-adjustment-form.component.scss'],
})
export class ClientAdjustmentFormComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  @Input()
  type: string;

  @Input()
  adjustment: Adjustment | Repo | SoldRented;

  @Input()
  project: Project;

  @Input()
  products: Product[];

  @Input()
  address: string;

  @Input()
  placementFloor: string;

  @Input()
  readOnly: boolean = false;

  @Input()
  client: boolean = false;

  @Input()
  projectId: number;

  @Input()
  projectContacts: ProjectContact[];

  @Input()
  secondBoard: SecondBoard;

  @Input()
  otherOrderPlacementInfo: string;

  @Input()
  minDate: Date;

  @Input()
  offlineUpload: boolean = true;

  @Input()
  isExecuted: boolean = false;

  @Output()
  adjustmentOut = new EventEmitter();

  @Output()
  save = new EventEmitter<any>();

  @Output()
  repoByInpress = new EventEmitter<boolean>();

  EX_EDIT_ADJUSTMENT_KEY = EX_EDIT_ADJUSTMENT_KEY;

  STICKER_OPTIONS = ProductHelper.SOLD_RENTED_OPTIONS;
  OTHER_STICKER_ID = ProductHelper.OTHER_TITLE_ID;
  selectedSticker: TitleOption;
  otherSticker: string;

  ipBoard: IPBoard;

  projectHasIPBoards: boolean = false;
  showAppointmentWarning: boolean = false;

  disableInstallByInpress: boolean = false;

  arrows: IPBoardArrows;
  secondBoardPresent: boolean = false;
  otherOrderPlacementInfoDisplay: string;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private ngForage: NgForage
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.products != null && this.products.length > 0) {
      const firstProduct = this.products[0];
      if (ProductHelper.isIPBoard(firstProduct)) {
        this.ipBoard = <IPBoard>{ ...firstProduct };
        this.projectHasIPBoards = true;
      }
    }
    this.showAppointmentWarning =
      ProjectHelper.mustHaveAppointmentForPlacementFloor(this.placementFloor);
    this.calculateDisableInstallByInpress(this.adjustment.status);
    if (this.projectHasIPBoards) {
      this.calculateExtrasFromBaseProjectInfo();
    }
    this.initializeSubscriptions();
    this.adjustmentOut.emit(this.adjustment);
  }

  private initializeSubscriptions(): void {
    if (
      this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED &&
      this.adjustment.id != null
    ) {
      this.addSubscription(
        this.projectDisplayObservableService.getSubProjectStatusChangedSubscribable(),
        (event: SubProjectStatusChangedEvent) => {
          if (event != null && event.subProjectId === this.adjustment.id) {
            this.calculateDisableInstallByInpress(event.status);
          }
        }
      );
    }
    if (!this.client && this.projectHasIPBoards) {
      const extrasFromBaseProjectObservable = this.createObservableUntilDestroy(
        this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable(),
        this.projectDisplayObservableService.getProjectPlacementVariableChangedSubscribable()
      );
      this.addSubscription(extrasFromBaseProjectObservable, () =>
        this.calculateExtrasFromBaseProjectInfoForAdmin()
      );
    }
  }

  private calculateDisableInstallByInpress(status: string): boolean {
    if (this.adjustment.type !== SUB_PROJECT_TYPES.SOLDRENTED) {
      return;
    }
    this.disableInstallByInpress =
      ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(status);
  }

  private calculateExtrasFromBaseProjectInfo(): void {
    if (this.client) {
      this.calculateExtrasFromBaseProjectForClient();
    } else {
      this.calculateExtrasFromBaseProjectInfoForAdmin();
    }
  }

  private calculateExtrasFromBaseProjectForClient(): void {
    this.arrows = this.ipBoard == null ? null : this.ipBoard['arrows'];
    this.secondBoardPresent = this.secondBoard != null;
    this.otherOrderPlacementInfoDisplay = this.otherOrderPlacementInfo;
  }

  private calculateExtrasFromBaseProjectInfoForAdmin(): void {
    const baseProject =
      this.project == null ? null : ProjectHelper.getBaseProject(this.project);
    if (baseProject == null) {
      this.arrows = null;
      this.secondBoardPresent = false;
      this.otherOrderPlacementInfoDisplay = null;
      return;
    }
    if (
      this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED ||
      this.adjustment.type === SUB_PROJECT_TYPES.REPO
    ) {
      if (baseProject.projectPlacement != null) {
        this.secondBoardPresent =
          baseProject.projectPlacement.secondBoard != null;
      } else {
        this.secondBoardPresent = false;
      }
    }
    if (this.adjustment.type === SUB_PROJECT_TYPES.REPO) {
      const ipBoardPlacement =
        this.ipBoard == null
          ? null
          : <IPBoardPlacement>this.ipBoard.productPlacement;
      if (
        ipBoardPlacement == null ||
        !IPBoardHelper.hasArrows(ipBoardPlacement)
      ) {
        this.arrows = null;
      } else {
        this.arrows = ipBoardPlacement.arrows;
      }
      this.otherOrderPlacementInfoDisplay =
        baseProject.projectPlacement == null
          ? null
          : baseProject.projectPlacement.otherOrderPlacementInfo;
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.offlineUpload) {
      this.cleanupOfflineAssets();
    }
  }

  selectedStickerOptionChanged(): void {
    if (this.selectedStickerOptionChanged == null) {
      return;
    }
    (<SoldRented>this.adjustment).otherSticker =
      this.selectedSticker.id === ProductHelper.OTHER_TITLE_ID
        ? null
        : this.selectedSticker.label;
    this.emitSave();
  }

  setSignSticker() {
    if (this.ipBoard != null) {
      this.projectDisplayObservableService
        .getSubProjectDesignVariableChangedSubscribable()
        .next();
      this.emitSave();
    }
  }

  installByInpressChanged(value: boolean): void {
    (<SoldRented>this.adjustment).installByInpress = value;
    if (this.adjustment.projectPlacement != null) {
      this.adjustment.projectPlacement.placementDone =
        value == false ? true : null;
      this.projectDisplayObservableService
        .getProjectPlacementExecutedSubscribable()
        .next(this.adjustment.id);
      this.projectDisplayObservableService
        .getInstalledByInpressSubscribable()
        .next(this.adjustment.id);
    }
    this.emitSave();
  }

  repoByInpressChanged(value: boolean): void {
    this.repoAdjustment.repoByInpress = value;
    this.repoByInpress.emit(value);
    this.emitSave();
    this.projectDisplayObservableService
      .getRepoByInpressSubscribable()
      .next(this.adjustment.id);
  }

  emitSave() {
    this.save.emit(this.adjustment);
  }

  compareOptions(o1: TitleOption, o2: TitleOption): boolean {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.id === o2.id;
    } else {
      return false;
    }
  }

  private async cleanupOfflineAssets(): Promise<void> {
    if (
      this.adjustment.projectPlacement != null &&
      CollectionHelper.isPopulated(
        this.adjustment.projectPlacement.projectPlacementAssets
      )
    ) {
      const promises =
        this.adjustment.projectPlacement.projectPlacementAssets.map((a) =>
          this.ngForage.removeItem(
            AssetHelper.getStorageKeyFor(a.localFileUuid)
          )
        );
      await Promise.all(promises);
    }
  }

  get repoAdjustment(): Repo {
    return this.adjustment as Repo;
  }

  get soldRentedAdjustment(): SoldRented {
    return this.adjustment as SoldRented;
  }
}
